import { styled } from '@linaria/react';
import type {
  AgentMinerSession,
  RelatedPrSummaryItem,
} from '../../../../api/agent-miner-client/agent-miner-client.type';
import {
  inkLight,
  secondaryBase,
  secondaryLight,
  secondaryLightest,
  skyDark,
  skyLightest,
} from '../../../../styles/design-tokens';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';

type RelatedPrsCellProps = {
  session: AgentMinerSession;
  /**
   * When true, renders every PR as its own chip on a wrapping row (used in the
   * roomier session side panel). Defaults to the compact first-chip + `+N`
   * overflow used by the table cell.
   */
  wrap?: boolean;
};

const prLabel = (pr: RelatedPrSummaryItem): string => (pr.number != null ? `#${pr.number}` : 'PR');

// Tooltip line: prefix the PR number with its repository so identical numbers
// across repos are distinguishable on hover.
const prTooltip = (pr: RelatedPrSummaryItem): string =>
  pr.repository_name ? `${pr.repository_name} ${prLabel(pr)}` : prLabel(pr);

/**
 * Sessions-list cell mirroring the work-units column: the first PR renders as a
 * number chip, with a `+N` overflow pill. Both carry a repo-qualified tooltip.
 * With `wrap`, all PRs render as wrapping chips instead. Renders an em dash when
 * the session fed no pull requests.
 *
 * @param {RelatedPrsCellProps} props - The row's session and layout mode
 * @returns {JSX.Element} The related-PRs cell
 */
export function RelatedPrsCell({ session, wrap = false }: RelatedPrsCellProps) {
  const prs = session.related_prs?.prs ?? [];
  if (prs.length === 0) {
    return <Dash>—</Dash>;
  }

  if (wrap) {
    return (
      <Wrapper $wrap>
        {prs.map((pr, index) => (
          <Tooltip
            key={`${pr.repository_name ?? ''}-${pr.number}-${index}`}
            label={prTooltip(pr)}
            variant="black"
            multiline
            style={{ maxWidth: 240 }}
          >
            <Chip>{prLabel(pr)}</Chip>
          </Tooltip>
        ))}
      </Wrapper>
    );
  }

  const [first, ...rest] = prs;
  const overflowLabel = rest.map(prTooltip).join('\n');

  return (
    <Wrapper>
      <Tooltip label={prTooltip(first)} variant="black" multiline style={{ maxWidth: 240 }}>
        <Chip>{prLabel(first)}</Chip>
      </Tooltip>
      {rest.length > 0 && (
        <Tooltip label={overflowLabel} variant="black" multiline style={{ maxWidth: 240, whiteSpace: 'pre-line' }}>
          <Overflow tabIndex={0} aria-label={overflowLabel}>
            +{rest.length}
          </Overflow>
        </Tooltip>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.span<{ $wrap?: boolean }>`
  flex-wrap: ${(props) => (props.$wrap ? 'wrap' : 'nowrap')};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border: 1px solid ${secondaryLight};
  background: ${secondaryLightest};
  color: ${secondaryBase};
  font: 500 11px/1.2 'Figtree', sans-serif;
  border-radius: 16px;
  white-space: nowrap;
`;

const Overflow = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  background: ${skyLightest};
  color: ${inkLight};
  font: 500 11px/1.2 'Figtree', sans-serif;
  border-radius: 16px;
  cursor: default;
`;

const Dash = styled(Text)`
  color: ${skyDark};
  font-size: 12px;
`;
